import React, { useEffect, useRef, useState } from 'react';
import { FaCamera, FaSpinner, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { stationService } from '../services/refuelService';
import { FuelStation } from '../types';
import { resizeImage } from '../utils/resizeImage';

// Alteração pendente da foto; só é enviada depois que o posto é salvo
export interface PhotoChange {
  file: File | null;
  removed: boolean;
}

export const noPhotoChange: PhotoChange = { file: null, removed: false };

/**
 * Aplica a alteração da foto depois que o posto foi salvo.
 * Retorna o posto atualizado, ou null quando não havia alteração ou o envio falhou (o erro já é avisado).
 */
export async function savePhotoChange(stationId: string, change: PhotoChange): Promise<FuelStation | null> {
  try {
    if (change.file) {
      return (await stationService.uploadPhoto(stationId, change.file)).data;
    }
    if (change.removed) {
      return (await stationService.deletePhoto(stationId)).data;
    }
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Posto salvo, mas não foi possível atualizar a foto');
  }
  return null;
}

/** URL para exibir a foto: a escolhida (ainda não enviada) ou a atual do posto */
export function usePhotoPreview(change: PhotoChange, currentUrl?: string | null): string | null {
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!change.file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(change.file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [change.file]);

  return preview || (change.removed ? null : currentUrl || null);
}

interface StationPhotoFieldProps {
  currentUrl?: string | null;
  value: PhotoChange;
  onChange: (change: PhotoChange) => void;
}

const StationPhotoField: React.FC<StationPhotoFieldProps> = ({ currentUrl, value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const shownUrl = usePhotoPreview(value, currentUrl);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem (JPG, PNG ou WebP)');
      return;
    }
    setProcessing(true);
    try {
      onChange({ file: await resizeImage(file), removed: false });
    } catch {
      toast.error('Não foi possível ler esta imagem. Tente uma foto JPG ou PNG.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-32 h-24 rounded-xl overflow-hidden bg-[#0f1419] border border-white/10 flex items-center justify-center shrink-0">
        {processing ? (
          <FaSpinner className="animate-spin text-white/40" />
        ) : shownUrl ? (
          <img src={shownUrl} alt="Foto do posto" className="w-full h-full object-cover" />
        ) : (
          <FaCamera className="text-white/30" size={24} />
        )}
      </div>
      <div className="flex flex-col items-start gap-2">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} />
        <button
          type="button"
          disabled={processing}
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-white/10 text-white/80 hover:border-white/30 disabled:opacity-60"
        >
          <FaCamera /> {shownUrl ? 'Trocar foto' : 'Escolher foto'}
        </button>
        {shownUrl && (
          <button
            type="button"
            onClick={() => onChange({ file: null, removed: !!currentUrl })}
            className="flex items-center gap-2 text-xs text-red-300/80 hover:text-red-300"
          >
            <FaTrash /> Remover foto
          </button>
        )}
        <p className="text-xs text-white/40">JPG, PNG ou WebP. Aparece no aplicativo junto com o posto.</p>
      </div>
    </div>
  );
};

export default StationPhotoField;
